import { useParams } from 'react-router-dom'
import { AnexoType, ButtonType, showAlert, showError } from '../../../../_metronic/helpers'
import { useCallback, useEffect, useState } from 'react'
import { checkToken, sendAnexo, sendProcedimentos } from './core/_request'
import {
  Button,
  FormFileInput,
  FormInput,
  ListLoading,
  LoadingIndicator,
} from '../../../components'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useT } from '_metronic/i18n/Metronici18n'

const validationSchema = Yup.object().shape({
  descricao: Yup.string().required('A descrição é obrigatória'),
  file: Yup.mixed().when('tipoAnexo', {
    is: (val: AnexoType) => val !== AnexoType.Procedimento,
    then: Yup.mixed()
      .required('O arquivo é obrigatório.')
      .test('fileType', 'Somente imagens são permitidas', (file) => {
        if (!file) return false
        return ['image/jpeg', 'image/png', 'image/gif'].includes(file.type)
      }),
    otherwise: Yup.mixed().notRequired(),
  }),
  files: Yup.array().when('tipoAnexo', {
    is: (val: AnexoType) => val === AnexoType.Procedimento,
    then: Yup.array()
      .of(
        Yup.mixed()
          .required('O arquivo é obrigatório.')
          .test('fileType', 'Somente imagens ou PDF são permitidos', (file) => {
            if (!file) return false
            return ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'].includes(file.type)
          })
      )
      .min(1, 'Selecionar pelo menos 1 arquivo.'),
    otherwise: Yup.array().notRequired(),
  }),
})

export const UploadPage = () => {
  const { t } = useT()
  const { tokenUpload } = useParams()

  const [checkingToken, setCheckingToken] = useState(true)
  const [authorized, setAuthorized] = useState(false)

  const formik = useFormik<{
    descricao: string
    file: File | null
    files: File[]
    tipoAnexo?: AnexoType
  }>({
    initialValues: {
      descricao: '',
      file: null,
      files: [] as File[],
      tipoAnexo: undefined,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (tokenUpload) {
        setSubmitting(true)
        try {
          if (values.files && values.tipoAnexo && values.tipoAnexo === AnexoType.Procedimento) {
            await sendProcedimentos(tokenUpload, values.files, values.descricao)
            showAlert({
              title: 'Sucesso!',
              text: 'Procedimento enviado com sucesso!',
              icon: 'success',
            })
          } else {
            await sendAnexo(tokenUpload, values.descricao, values.file as File)
            showAlert({ title: 'Sucesso!', text: 'Imagem enviada com sucesso!', icon: 'success' })
          }
        } catch (error) {
          showError(error)
        } finally {
          setSubmitting(false)
        }
      }
    },
  })

  const getToken = useCallback(async () => {
    if (tokenUpload) {
      try {
        const data = await checkToken(tokenUpload)
        formik.setFieldValue('tipoAnexo', data.tipoAnexo?.id)
        setAuthorized(true)
      } finally {
        setCheckingToken(false)
      }
    }
  }, [formik, tokenUpload])

  useEffect(() => {
    getToken()
  }, [getToken])

  return (
    <>
      {checkingToken && <ListLoading />}
      {!checkingToken && !authorized && (
        <h1>
          Sua sessão expirou ou o token de acesso é inválido. Por favor, gere um novo para
          continuar.
        </h1>
      )}
      {authorized && (
        <form onSubmit={formik.handleSubmit}>
          <FormFileInput
            formik={formik}
            name={formik.values.tipoAnexo === AnexoType.Procedimento ? 'files' : 'file'}
            className='mb-2'
            label={formik.values.tipoAnexo === AnexoType.Procedimento ? 'Arquivos' : 'Imagem'}
            isRequired
            isDisabled={formik.isSubmitting}
            multiple={formik.values.tipoAnexo === AnexoType.Procedimento}
          />
          <FormInput
            formik={formik}
            name='descricao'
            label='Descrição'
            isRequired
            placeholder='Digite a descrição da imagem'
            className='mb-2'
          />
          <Button
            type={ButtonType.Submit}
            disabled={formik.isSubmitting}
            className='btn-primary'
            size='btn-sm'
          >
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
          </Button>
        </form>
      )}
    </>
  )
}
